import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import AttrValue from 'models/AttrValue';
import { WorkflowAction } from 'models/WorkflowAction';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { REFERRAL_SOURCE_SELECT_STYLES, VALUE_NAMES } from './constants';

/**
 * The one state this step ever shows a person (OLIO-3158): a referral named a source we don't
 * recognize, so reporting has nowhere to file it until someone says what it means.
 *
 * The panel is an exception by design. Anything the client already has a name for resolves
 * without asking and the step parks unseen, which is why this is worth a moment when it does
 * appear — and why it asks for exactly one thing and nothing else.
 */

// What the API records on the action while it waits: one entry per value it couldn't place.
//
// Deliberately NOT the list of choices. The API sends one, built when the step first asked, and
// reading it is what OLIO-3158 shipped with — a snapshot frozen at that moment, so a referral
// source an admin added while the question sat open never appeared in the picker (QA, Sep 1).
// The choices are the client's configuration rather than evidence about this referral, and they
// can change while the card waits, so they are fetched below instead.
export interface OutstandingSource {
  attrId: string;
  attrName: string;
  rawValue: string;
}

const NEW_SOURCE = '__new__';

// The "add one" row, shaped as an AttrValue so it can ride the same list as the real ones. Same
// trick ReferralModal uses for the referral's own stored wording. Module-level so its identity is
// stable — Select memoizes its option list on this array.
const ADD_NEW_OPTIONS = [{ id: NEW_SOURCE, name: 'Add a new referral source…' } as AttrValue];

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  max-width: 480px;
`;

const Found = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${colors.black5};
`;

// The packet's own wording, quoted rather than tidied: the coordinator is being asked to
// recognize it, and a cleaned-up version is a different string than the one on the document.
const RawValue = styled(BodySmallBold)`
  word-break: break-word;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default function ConfirmReferralSource({
  action,
  outstanding,
  onComplete,
  isCompleting,
}: {
  action: WorkflowAction;
  outstanding: OutstandingSource[];
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => unknown;
  isCompleting: boolean;
}) {
  // One question at a time. The API contract allows several, but referral intake declares a single
  // value, so a form for N would be scaffolding for a case that does not exist.
  const question = outstanding[0];
  const [picked, setPicked] = useState<AttrValue | null>(null);
  const [typed, setTyped] = useState('');

  // The client's own referral sources, asked for when the coordinator opens the picker rather than
  // taken from the action's recorded question. The same hook and the same arguments the create
  // form's picker uses, so the two lists cannot disagree.
  //
  // `enabled: false` is not a mistake: Select's onMenuOpen pulls the first page, then typing
  // narrows and scrolling pages. Deduplicated by display name server-side, since several raw
  // wordings share one reporting name and a picker must offer it once.
  // `visible` matters here beyond tidiness: picking a retired name records a Raw Value pointing at
  // it, after which every future referral worded that way resolves to it silently.
  const referralSources = useInfiniteAttrValues({
    workflowValueName: VALUE_NAMES.REFERRAL_SOURCE,
    distinctDisplayName: true,
    active: true,
    visible: true,
  });

  if (!question) return null;

  const isNew = picked?.id === NEW_SOURCE;
  // `name` on this endpoint IS the reporting name — attr_values#index serializes display_name into
  // it — which is what the answer has to carry.
  const displayName = isNew ? typed.trim() : (picked?.name ?? '');

  const confirm = () => {
    if (!displayName) return;
    // camelCase on the way out too, which OliveBranch snake_cases before the API sees it. Sending
    // snake_case happens to survive the transform, but relying on that is how the read side broke.
    onComplete(action.id, {
      attrResolutions: [{ rawValue: question.rawValue, displayName }],
    });
  };

  return (
    <Panel data-testid='confirm-referral-source'>
      <Found>
        <BodySmall>This referral says it came from</BodySmall>
        <RawValue data-testid='referral-source-raw'>“{question.rawValue}”</RawValue>
      </Found>

      {/* One line rather than the two it used to pick between. The old copy had a "you have no
          referral sources yet" variant, chosen from a count the payload carried; the list is not
          counted until the coordinator opens the picker now, and copy that rewrites itself on open
          is worse than copy that reads correctly either way. A client with none sees the one row
          that matters. */}
      <BodySmall>
        Which of your referral sources is that? Referrals worded this way will match it from now on.
      </BodySmall>

      <Field>
        <Select<AttrValue>
          {...referralSources}
          appendedOptions={ADD_NEW_OPTIONS}
          value={picked}
          // Naming a new source starts from the wording the referral actually used, since that is
          // usually most of the answer — "ST MARYS HOSPITAL" wants tidying, not retyping. Only when
          // the box is empty, so switching away and back never overwrites what they typed.
          onChange={(option) => {
            setPicked(option);
            if (option?.id === NEW_SOURCE && !typed) setTyped(question.rawValue);
          }}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          placeholder='Select a referral source'
          dataTestId='referral-source-choice'
          disabled={isCompleting}
          menuPortalTarget={document.body}
          menuPlacement='auto'
          maxMenuHeight={240}
          styles={REFERRAL_SOURCE_SELECT_STYLES}
        />

        {isNew && (
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder='Name this referral source'
            disabled={isCompleting}
            data-testid='referral-source-new'
          />
        )}
      </Field>

      <div>
        <Button
          variant='primary'
          size='small'
          disabled={!displayName || isCompleting}
          onClick={confirm}
          data-testid='referral-source-confirm'>
          Confirm
        </Button>
      </div>
    </Panel>
  );
}
